import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { translate } from '@/utils';
import * as R from '@/Router/routes';

type PathnameType = (typeof R)[keyof typeof R];

type PageConfigType = {
  isMenuVisible?: boolean;
  hideHeader?: boolean;
  isBackButtonVisible?: boolean;
  mobileHeading?: string;
  hasLogout?: boolean;
  backUrl?: string;
  mainStyle?: string;
  customStyle?: string;
  footerStyle?: string;
  layoutBodyStyle?: string;
  mobileContent?: {
    heading?: string;
    style?: string;
    textStyle?: string;
  };
};

const pageConfig: Partial<Record<Partial<PathnameType>, PageConfigType>> = {
  '/login': {},
  '/2fa': {
    backUrl: '/login?from2fa=true',
  },
  '/password-recovery': {
    backUrl: R.LOGIN,
  },
  '/password-recovery/sent': {
    backUrl: R.LOGIN,
  },
  '/password-recovery/new-password/done': {
    backUrl: R.LOGIN,
  },
  '/account': {
    isMenuVisible: true,
    isBackButtonVisible: false,
    mobileHeading: 'common:greeting',
  },
  '/account/settings': {
    isMenuVisible: true,
    mobileHeading: 'profile:heading',
  },
  '/account/settings/change-address': {
    isMenuVisible: true,
    mobileHeading: 'profile:heading',
  },
  '/account/settings/change-bank-account': {
    isMenuVisible: true,
    mobileHeading: 'profile:heading',
  },
  '/account/settings/change-email': {
    isMenuVisible: true,
    mobileHeading: 'profile:heading',
  },
  '/account/settings/change-email-confirm': {
    isMenuVisible: true,
    mobileHeading: 'profile:heading',
  },
  '/account/settings/change-email-done': {
    isMenuVisible: true,
    mobileHeading: 'profile:heading',
  },
  '/account/settings/change-phone': {
    isMenuVisible: true,
    mobileHeading: 'profile:heading',
  },
  '/account/settings/change-password': {
    isMenuVisible: true,
    mobileHeading: 'profile:heading',
  },
  '/account/settings/change-password-done': {
    isMenuVisible: true,
    mobileHeading: 'profile:heading',
  },
  '/account/repayment/custom': {
    isMenuVisible: true,
    backUrl: R.ACTIVE_LOAN,
    mobileHeading: 'payments:repayment_subtitle:custom',
  },
  '/account/repayment/minimum': {
    isMenuVisible: true,
    backUrl: R.ACTIVE_LOAN,
    mobileHeading: 'payments:repayment_subtitle:minimum',
  },
  '/account/repayment': {
    isMenuVisible: true,
    backUrl: R.ACTIVE_LOAN,
    mobileHeading: 'payments:repayment_subtitle:full',
  },
  '/account/repeated-loan': {
    isMenuVisible: true,
    mobileHeading: 'repeated_loan:heading',
  },
  '/account/repeated-loan/account-confirm': {
    backUrl: R.REPEATED_LOAN,
    mobileHeading: 'psd2_account_confirm:heading_mobile',
  },
  '/account/repeated-loan/account-instructions': {
    backUrl: R.PSD2_ACCOUNT_CONFIRM,
  },
  '/account/repeated-loan/personal-data-confirm': {
    backUrl: R.PSD2_ACCOUNT_CONFIRM,
    mobileHeading: 'personal_data_confirm:heading',
  },
  '/account/repeated-loan/budget': {
    backUrl: R.PERSONAL_DATA_CONFIRM,
    mobileHeading: 'personal_data_confirm:heading',
  },
  '/account/repeated-loan/new-offer-decreased': {
    mobileHeading: 'new_offer_decreased:best_offer',
  },
  '/account/repeated-loan/new-offer-increased': {
    mobileHeading: 'new_offer_increased:mobile_heading',
  },
  '/account/repeated-loan/confirm': {
    mobileHeading: 'loan_confirm:mobile_heading',
  },
  '/account/repeated-loan/approve': {
    mobileHeading: 'loan_approved:mobile_heading',
  },
  '/account/additional-amount': {
    mobileHeading: 'additional_amount:heading_mobile',
    backUrl: R.ACTIVE_LOAN,
  },
  '/account/additional-amount/budget': {
    backUrl: R.ADDITIONAL_AMOUNT,
    mobileHeading: 'personal_data_confirm:heading',
  },
  '/account/additional-amount/confirm': {
    mobileHeading: 'loan_confirm:mobile_heading',
  },
  '/account/additional-amount/approve': {
    mobileHeading: 'additional_amount_approved:mobile_heading',
  },
  '/account/promo': {
    customStyle:
      'md:py-0 p-0 min-h-1000 md:min-h-full lg:min-h-full max-w-full',
    isMenuVisible: false,
    isBackButtonVisible: false,
    backUrl: R.ACTIVE_LOAN,
    mobileHeading: 'promo:heading_mobile',
  },
  '/account/top-up-amount': {
    backUrl: R.ACTIVE_LOAN,
    mobileHeading: 'top_up_amount:heading_mobile',
  },
  '/account/pending': {
    isMenuVisible: true,
    mobileHeading: 'pending_loan:header',
  },
  '/account/overdue': {
    isMenuVisible: true,
    mobileHeading: 'common:greeting',
  },
  '/account/overdue/debt-schedule': {
    isMenuVisible: true,
  },
  '/account/broken-agreement': {
    isMenuVisible: true,
    mobileHeading: 'common:greeting',
  },
  '/account/repeated-loan/document-upload': {
    mobileHeading: 'document_upload:heading',
  },
  '/account/repeated-loan/document-upload/send-later': {
    mobileHeading: 'document_upload:heading',
  },
  '/account/repeated-loan/kyc-upload': {
    mobileHeading: 'kyc_upload:heading',
  },
  '/account/repeated-loan/kyc-pending': {
    mobileHeading: 'document_upload:heading',
  },
  '/account/repeated-loan/psd/select-bank': {
    mobileHeading: 'psd2_banks:heading_mobile',
    isBackButtonVisible: false,
  },
  '/account/repeated-loan/psd/select-bank/helper': {
    mobileHeading: 'psd2_banks:heading_mobile',
  },
  '/account/repeated-loan/psd/consent': {
    mobileHeading: 'psd2_consents:heading_mobile',
    backUrl: R.PSD2_BANKS,
  },
  '/account/repeated-loan/psd/select-account': {
    mobileHeading: 'psd2_bank_account_select:heading_mobile',
  },
  '/account/repeated-loan/psd/verified': {
    mobileHeading: 'psd2_verified:heading_mobile',
  },
  '/account/repeated-loan/psd/approved': {
    mobileHeading: 'psd2_verified:heading_mobile',
  },
  '/faq': {
    isMenuVisible: true,
    backUrl: R.ACTIVE_LOAN,
    isBackButtonVisible: true,
    mobileHeading: 'faq:heading_mobile',
  },
  '/loan-examples': {
    isMenuVisible: true,
    backUrl: R.ACTIVE_LOAN,
    isBackButtonVisible: true,
    mobileHeading: 'loan_examples:heading_mobile',
  },
  // WITHDRAW ROUTES
  '/account/withdraw-intro-confirm': {
    mainStyle: 'max-w-4xl',
    mobileContent: {
      heading: translate('withdraw_contract_flow.introConfirm.title'),
    },
  },
  '/account/withdraw-warning': {
    mainStyle: 'max-w-4xl',
    mobileContent: {
      heading: translate('withdraw_contract_flow.introConfirm.title'),
    },
  },
  '/account/withdraw-consents': {
    mainStyle: 'max-w-4xl',
    mobileContent: {
      heading: translate('withdraw_contract_flow.introConfirm.title'),
    },
  },
  '/account/withdraw-dashboard': {
    mainStyle: 'max-w-3xl',
    isMenuVisible: true,
    mobileContent: {
      heading: translate('withdraw_contract_flow.dashboardWithdraw.title'),
    },
  },
  '/account/withdraw-dashboard-broken': {
    hideHeader: true,
    layoutBodyStyle: 'bg-gradient-to-b from-[#ff4131] to-[#F1A3A3]',
    footerStyle: 'mt-0',
    mainStyle: 'max-w-4xl mt-3',
    mobileContent: {
      heading: translate('withdraw_contract_flow.dashboardWithdraw.title'),
    },
  },
};

export default function useLayoutConfig() {
  const location = useLocation();
  const { t } = useTranslation();

  const config = pageConfig[location.pathname as PathnameType];
  if (config && config.mobileHeading)
    return { ...config, mobileHeading: t(config.mobileHeading) };

  return config ?? {};
}
